import { CountryCode } from '@dayetopia/types';
import * as Sentry from '@sentry/react';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { CartItem } from '@contracts';
import { getProductTitle } from '@data/products';
import getOmsUrl from '@utils/getOmsUrl';
import { getProductPricesFromStorage } from '@utils/prices';
import { isProductAvailableInCountry } from '@utils/product';

interface ErrorCallback {
  (error: any): void;
}

/**
 * Checks if all products in the cart are available in the specified country.
 *
 * @param cartItems Cart items to check for availability
 * @param deliveryCountry Country code to check availability against
 * @param setErrorMessage Function to set error message if products are unavailable
 * @param setCheckoutEnabled Optional function to enable/disable checkout
 * @param t Translation function
 * @returns boolean indicating if all products are available
 */
export const checkProductsEligible = (
  cartItems: CartItem[],
  deliveryCountry: CountryCode,
  setErrorMessage: (message: string) => void,
  setCheckoutEnabled?: (enabled: boolean) => void,
  t?: (key: string, params?: object) => string
): boolean => {
  // TODO: Implement this logic once we decide on the format for the data
  return true;

  const pricesData = getProductPricesFromStorage();

  // Check each cart item's availability in the selected country
  const unavailableProducts = cartItems.filter((item) => {
    const product = pricesData.find((p) => p.id === item.id);

    if (!product) {
      return true; // If product not found, consider it unavailable
    }

    const isAvailable = isProductAvailableInCountry(product, deliveryCountry);
    return !isAvailable;
  });

  if (unavailableProducts.length > 0) {
    const productNames = unavailableProducts
      .map((item) => {
        const name = getProductTitle(item.productCode) || item.id;
        return name;
      })
      .join(', ');

    const errorMessage = t
      ? t('components.products-not-available', { products: productNames }) || ''
      : `These products are not available in your country: ${productNames}`;

    setErrorMessage(errorMessage);

    if (setCheckoutEnabled) {
      setCheckoutEnabled(false);
    }

    return false;
  }

  // Reset checkout state if products are available
  setErrorMessage('');

  if (setCheckoutEnabled) {
    setCheckoutEnabled(true);
  }

  return true;
};

export default class Checkout {
  private readonly omsUrl?: string;

  private readonly idToken: string;

  private readonly errorCallback: ErrorCallback;

  constructor(idToken: string, errorCallback: ErrorCallback) {
    if (!idToken) {
      throw new Error('idToken is required.');
    }
    this.omsUrl = getOmsUrl();
    this.idToken = idToken;
    this.errorCallback = (error) => errorCallback(error);
  }

  private handleError(error: any) {
    console.debug('Error: ', JSON.stringify(error));
    Sentry.captureException(error);
    this.errorCallback(error);
  }

  private getAxiosConfig(): AxiosRequestConfig {
    return {
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        token: this.idToken
      }
    };
  }

  public async createOrder(body: any, isPreviewOrder?: (order: any) => void): Promise<any> {
    const url = isPreviewOrder ? `${this.omsUrl}/orders/preview` : `${this.omsUrl}/orders`;
    try {
      const response = await axios.post(url, body, this.getAxiosConfig());

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const { response } = error as AxiosError;
        if (response?.data?.error) {
          const errorMessage = JSON.stringify(response.data.error);
          console.error('Checkout Error: ', errorMessage);
          throw new Error(errorMessage);
        }
      }
      this.handleError(error);
      throw new Error('An error occurred while making the API request.');
    }
  }

  public async getOrderById(orderId: string): Promise<any> {
    const url = `${this.omsUrl}/orders/${orderId}`;
    try {
      const response = await axios.get(url, this.getAxiosConfig());
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const { response } = error as AxiosError;
        if (response?.data?.error) {
          const errorMessage = JSON.stringify(response.data.error);
          console.error('Get Order Error: ', errorMessage);
          throw new Error(errorMessage);
        }
      }
      this.handleError(error);
      throw new Error('An error occurred while fetching the order.');
    }
  }
}
